// src/pages/HistoryTimeline.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Box, Container, Typography, Paper, Stack, Slide } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getPrimaryColor } from "../../../../GlobalFunction/Theme";

/* --------- language text for page title / subtitle / intro --------- */
const TIMELINE_TEXT = {
  ZH: {
    title: "水禾歷史",
    subtitle: "從MIZUNOGI到SEHO",
    introLines: [
      "一個彼此包容關懷的社會，一個尊重個性與創造力的自由社會，",
      "一個每個人都能共同享受充實生活的快樂社會，水禾所憧憬的社會，",
      "是一個每個人都能共同美好生活的社會。水禾正在創造更幸福的生活。",
    ],
  },
  EN: {
    title: "SEHO History",
    subtitle: "From MIZUNOGI to SEHO",
    introLines: [
      `"A compassionate society,"`,
      `"a society that celebrates individuality and creativity"`,
      `"a joyful society where everyone shares in a fulfilling life"`,
      "This is the kind of society SEHO envisions.",
      "Step by step, we are working to build a happier way of living for all.",
    ],
  },
  JP: {
    title: "水禾の歩み",
    subtitle: "MIZUNOGIからSEHOへ、ブランド名の引き継ぎ",
    introLines: [
      "互いに思いやり支え合う社会、個性と創造性が尊重される自由な社会、",
      "一人ひとりが充実した暮らしを分かち合える喜びに満ちた社会——それが水禾のめざす社会です。",
      "当社は、その実現に向けて、より幸せな暮らしを創造してまいります。",
    ],
  },
};

function getTimelineText(lang = "ZH") {
  return TIMELINE_TEXT[lang] || TIMELINE_TEXT.ZH;
}

/* --------- history data (period + hero + detail) per language --------- */

const HISTORY_DATA_BY_LANG = {
  ZH: [
    {
      period: "創立期",
      years: "2015",
      image: [
        { desktop: "/img/aboutus/history/MIZUNOGI.jpg" },
        { mobile: "/img/aboutus/history/MIZUNOGI-mobile.jpg" },
      ],
      detail: [
        {
          years: "2015",
          title: "MIZUNOGI品牌誕生",
          image: "/img/aboutus/history/2015-mizunogi.webp",
          content: [
            "清潔衛生業的台灣水禾實業的成立，",
            "標誌著清潔的現代化和生活水準的提高迎來了契機。",
            "MIZUNOGI自有品牌PVC門簾與代理SARAYA清潔品牌",
            "也在台灣食品衛生清潔的開發和進步中發揮了引領作用。",
          ],
        },
        {
          years: "2017",
          title: "專業食品衛生服務的擴展",
          content: [
            "MIZUNOGI自有品牌除塵黏紙、捕蟲燈與代理Dyson乾手機，",
            "食品衛生與病媒防治的專業服務。",
            "水禾開始快速發展成為服務食品廠的專業供應商。",
          ],
          image: "/img/aboutus/history/2017-dyson.webp",
        },
      ],
    },
    {
      period: "突破期",
      years: "2018",
      image: [
        { desktop: "/img/aboutus/history/2018.webp" },
        { mobile: "/img/aboutus/history/2018-MOBILE.webp" },
      ],
      detail: [
        {
          years: "2018",
          title: "電潔水品牌誕生",
          content: [
            "食品業清潔衛生的現代化年代，",
            "水禾為滿足食品大廠帶來的日益複雜多樣的需求，",
            "客製與全方位服務。同時，水禾也致力於業務多元化，",
            "夯實基礎，力求成為專注清潔衛生服務，守護環境的企業，",
            "電潔水品牌於此誕生，在宜得利通路上架，",
            "也協辦台灣第一屆營養午餐大賽，",
            "讓食品衛生營養健康息息相關的觀念往下紮根。",
          ],
          image: "/img/Logo/brand/water-cleaner.png",
        },
        {
          years: "2019",
          title: "擦拭君品牌誕生",
          content: ["水禾推出了擦拭君品牌，一個食品級的抹布品牌誕生。"],
          image: "/img/Logo/brand/wipe.png",
        },
        {
          years: "2020",
          title: "IRIS與日東代理及金炬獎肯定",
          content: "水禾代理了IRIS與日東品牌，並榮獲第十六屆十大金炬獎的殊榮。",
          image: "/img/aboutus/history/iris.webp",
        },
      ],
    },
    {
      period: "成長期",
      years: "2023",
      image: [
        { desktop: "/img/aboutus/history/2023.webp" },
        { mobile: "/img/aboutus/history/2023-mobile.webp" },
      ],
      children: [
        {
          years: "2023",
          title: "成為食品衛生與病媒防治領導品牌",
          content: [
            "讓食品衛生的防護更完整。水禾憑藉其累積的資本和技術，",
            "在清潔衛生和食品、飯店、零售行業，確立了無與倫比的競爭力，",
            "成為食品衛生與病媒防治領導品牌。",
          ],
          image: "/img/aboutus/history/2023-green.webp",
        },
      ],
    },
    {
      period: "擴張期",
      years: "2026",
      image: [
        { desktop: "/img/aboutus/history/2026.webp" },
        { mobile: "/img/aboutus/history/2026-mobile.webp" },
      ],
      children: [
        {
          years: "2026",
          title: "全新SEHO品牌與您見面",
          content: [
            "在快速發展年代的基礎上，水禾制定了面向未來的長期策略，",
            "拓展了業務領域，並為進軍全球市場奠定了基礎。",
          ],
          image: "/img/aboutus/history/SEHO_LOGO1.webp",
        },
      ],
    },
  ],

  EN: [
    {
      period: "Founding Period",
      years: "2015",
      image: [
        { desktop: "/img/aboutus/history/MIZUNOGI.jpg" },
        { mobile: "/img/aboutus/history/MIZUNOGI-mobile.webp" },
      ],
      detail: [
        {
          years: "2015",
          title: "Birth of the MIZUNOGI Brand",
          image: "/img/aboutus/history/2015-mizunogi.webp",
          content: [
            "The founding of Taiwan SEHO signaled new opportunities for modernizing hygiene standards and elevating quality of life.",
            "Through our MIZUNOGI PVC curtain brand and the distribution of SARAYA cleaning solutions, we have helped drive progress in Taiwan's food hygiene and sanitation industries.",
          ],
        },
        {
          years: "2017",
          title: "Expansion of Professional Food Hygiene Services",
          content: [
            "With MIZUNOGI hygiene tools and Dyson hand dryers,",
            "we enhanced our food-hygiene and pest-control services.",
            "Taiwan SEHO quickly became a trusted supplier to food-processing companies.",
          ],
          image: "/img/aboutus/history/2017-dyson.webp",
        },
      ],
    },
    {
      period: "Breakthrough Period",
      years: "2018",
      image: [
        { desktop: "/img/aboutus/history/2018.webp" },
        { mobile: "/img/aboutus/history/2018-MOBILE.webp" },
      ],
      detail: [
        {
          years: "2018",
          title: "Launch of the 電潔水【Water Cleaner】Brand",
          content: [
            "With rising hygiene standards, Taiwan SEHO strengthened its customized service solutions for major food manufacturers.",
            "It introduced its 電潔水【Water Cleaner】brand in Nitori and co-organized Taiwan's first National School Lunch Competition.",
          ],
          image: "/img/Logo/brand/water-cleaner.png",
        },
        {
          years: "2019",
          title: "Launch of the 擦拭君 Brand",
          content: [
            "Taiwan SEHO launched the 擦拭君 brand,",
            "introducing a new line of food-grade wiping cloths.",
          ],
          image: "/img/Logo/brand/wipe.png",
        },
        {
          years: "2020",
          title: "IRIS & NITTO Distribution and Golden Torch Award",
          content:
            "Taiwan SEHO became an authorized distributor for IRIS and NITTO brands and received the honor of the 16th Top Ten Golden Torch Award.",
          image: "/img/aboutus/history/iris.webp",
        },
      ],
    },
    {
      period: "Growth Period",
      years: "2023",
      image: [
        { desktop: "/img/aboutus/history/2023.webp" },
        { mobile: "/img/aboutus/history/2023-mobile.webp" },
      ],
      children: [
        {
          years: "2023",
          title: "Becoming a Leading Brand in Food Hygiene and Vector Control",
          content: [
            "Making food hygiene protection more comprehensive. ",
            "Leveraging its accumulated capital and technology, ",
            "Taiwan SEHO has established unparalleled competitiveness in the cleaning and hygiene, food, restaurant, and retail industries, ",
            "becoming a leading brand in food hygiene and vector control.",
          ],
          image: "/img/aboutus/history/2023-green.webp",
        },
      ],
    },
    {
      period: "Expansion Period",
      years: "2026",
      image: [
        { desktop: "/img/aboutus/history/2026.webp" },
        { mobile: "/img/aboutus/history/2026-mobile.webp" },
      ],
      children: [
        {
          years: "2026",
          title: "The New SEHO Brand Meets the Market",
          content: [
            "With its rapid development as a foundation, Taiwan SEHO set forward-looking long-term strategies, ",
            "broadened its business fields, and prepared for global expansion.",
          ],
          image: "/img/aboutus/history/SEHO_LOGO1.webp",
        },
      ],
    },
  ],

  JP: [
    {
      period: "創立期",
      years: "2015",
      image: [
        { desktop: "/img/aboutus/history/MIZUNOGI.jpg" },
        { mobile: "/img/aboutus/history/MIZUNOGI-mobile.webp" },
      ],
      detail: [
        {
          years: "2015",
          title: "MIZUNOGIブランド誕生",
          image: "/img/aboutus/history/2015-mizunogi.webp",
          content: [
            "衛生・清掃分野における台湾水禾実業有限会社(MIZUNOGI)の設立は、",
            "清掃の近代化と生活水準の向上に向けた新たな契機となりました。",
            "自社ブランドの「MIZUNOGI PVCカーテン」と、代理店として取り扱うSARAYAのシリーズ清掃商品は",
            "台湾の食品分野における衛生・清潔管理の発展をリードしてまいりました。",
          ],
        },
        {
          years: "2017",
          title: "食品衛生サービスの本格展開",
          content: [
            "MIZUNOGIブランドの粘着クリーナーや捕虫器に加え、",
            "Dysonハンドドライヤーの取り扱いを開始し、",
            "食品衛生分野と防虫対策に関する専門サービスを展開しました。",
            "水禾は、大手食品工場を支える専門サプライヤーとして、",
            "事業を急速に成長し始めました。",
          ],
          image: "/img/aboutus/history/2017-dyson.webp",
        },
      ],
    },
    {
      period: "突破期",
      years: "2018",
      image: [
        { desktop: "/img/aboutus/history/2018.webp" },
        { mobile: "/img/aboutus/history/2018-MOBILE.webp" },
      ],
      detail: [
        {
          years: "2018",
          title: "サブブランド；アルカリ電潔水の登場",
          content: [
            "食品業界の衛生管理が本格的に近代化していく中、",
            "水禾は大手食品メーカーの多様で複雑化するニーズにお応えするため、",
            "カスタマイズされたトータルソリューションを提供してまいりました。",
            "同時に、事業の多角化と基盤強化にも取り組み、",
            "環境を守る衛生サービス専門企業を目指してまいりました。",
            "その流れの中で、100％純水を使用したでんかい水「電潔水」サブブランドが誕生し、NITORI(ニトリ)での取り扱いを開始いたしました。",
            "また、台湾において、初の全国学校給食のコンテストに協賛し、食品衛生、栄養、健康に関する意識を、学齢期の子供たちに小さい頃から、根付かせる活動を行ってまいりました。",
          ],
          image: "/img/Logo/brand/water-cleaner.png",
        },
        {
          years: "2019",
          title: "サブブランド「擦拭君」が登場",
          content: [
            "水禾は食品グレードのふきんブランド「擦拭君」を立ち上げ、",
            "新たな製品カテゴリーを展開しました。",
          ],
          image: "/img/Logo/brand/wipe.png",
        },
        {
          years: "2020",
          title: "IRIS・日東ブランドの取り扱いと金炬賞受賞",
          content: [
            "IRISおよび日東ブランドの代理店となり、",
            "第16回「十大金炬賞」を受賞する栄誉にあずかりました。",
          ],
          image: "/img/aboutus/history/iris.webp",
        },
      ],
    },
    {
      period: "成長期",
      years: "2023",
      image: [
        { desktop: "/img/aboutus/history/2023.webp" },
        { mobile: "/img/aboutus/history/2023-mobile.webp" },
      ],
      children: [
        {
          years: "2023",
          title: "食品衛生と病害虫防除のリーディング会社へ",
          content: [
            "食品衛生の防護をより完璧なものにするため、",
            "当社はこれまでに蓄積した資本と技術を基盤に、",
            "清掃衛生分野、食品分野、ホテル業、そして小売業において、",
            "卓越した競争きょうそう力を確立いたしました。",
            "そして、食品衛生と病害虫防除分野における、",
            "リーディングブランドとしての地位を築いています。",
          ],
          image: "/img/aboutus/history/2023-green.webp",
        },
      ],
    },
    {
      period: "拡大期",
      years: "2026",
      image: [
        { desktop: "/img/aboutus/history/2026.webp" },
        { mobile: "/img/aboutus/history/2026-mobile.webp" },
      ],
      children: [
        {
          years: "2026",
          title: "新ブランド名『SEHO』始動",
          content: [
            "急速な成長期を基に、水禾は将来の長期的戦略を策定し、",
            "事業領域の拡大とともに、グローバル市場への本格進出に向けた土台を築いています。",
          ],
          image: "/img/aboutus/history/SEHO_LOGO1.webp",
        },
      ],
    },
  ],
};

function getHistoryData(lang = "ZH") {
  return HISTORY_DATA_BY_LANG[lang] || HISTORY_DATA_BY_LANG.ZH;
}

/* --------- helpers --------- */
const normalizeImagePath = (p) => {
  if (typeof p !== "string") return null;
  if (/\.(jpg|jpeg|png|webp|gif|avif)$/i.test(p)) return p;
  return p.replace(/(jpg|png|jpeg|webp|gif|avif)$/i, ".$1");
};

function makeImageSrc(path) {
  if (!path || typeof path !== "string") return { src: null, revoke: () => {} };
  const s = normalizeImagePath(path.trim());
  return { src: s, revoke: () => {} };
}

/** pick desktop/mobile hero image based on viewport */
function resolveHeroImagePath(image, isDesktop) {
  if (!image) return null;

  if (typeof image === "string") return image;

  if (!Array.isArray(image) && typeof image === "object") {
    const desktopPath = image.desktop || null;
    const mobilePath = image.mobile || null;
    return isDesktop ? desktopPath || mobilePath : mobilePath || desktopPath;
  }

  if (Array.isArray(image)) {
    let desktopPath = null;
    let mobilePath = null;

    image.forEach((entry) => {
      if (!entry) return;
      if (typeof entry === "string") {
        if (!desktopPath) desktopPath = entry;
      } else if (typeof entry === "object") {
        if (entry.desktop) desktopPath = entry.desktop;
        if (entry.mobile) mobilePath = entry.mobile;
      }
    });

    return isDesktop ? desktopPath || mobilePath : mobilePath || desktopPath;
  }

  return null;
}

/** safely resolve event/detail images (string / array / {desktop, mobile}) */
function resolveEventImagePath(image) {
  if (!image) return null;

  if (typeof image === "string") return image;

  if (!Array.isArray(image) && typeof image === "object") {
    return image.desktop || image.mobile || null;
  }

  if (Array.isArray(image)) {
    for (const entry of image) {
      if (!entry) continue;
      if (typeof entry === "string") return entry;
      if (typeof entry === "object") {
        if (entry.desktop || entry.mobile) {
          return entry.desktop || entry.mobile;
        }
      }
    }
  }

  return null;
}

/** render array-of-lines as line breaks, or plain string as-is */
function renderContentLines(content) {
  if (!content) return null;
  if (Array.isArray(content)) {
    return content.map((line, idx) => (
      <React.Fragment key={idx}>
        {line}
        {idx !== content.length - 1 && <br />}
      </React.Fragment>
    ));
  }
  return content;
}

// generic in-view hook
function useInView(
  options = { rootMargin: "0px 0px -10% 0px", threshold: 0.3 },
) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  const cb = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting) setInView(true);
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(cb, options);
    obs.observe(node);
    return () => obs.disconnect();
  }, [cb, options]);

  return { ref, inView };
}

/* --------- one row on main rail (image ⇄ text) --------- */
function HistoryEventRow({ item, isLeft, rail, fonts }) {
  const rowRef = useRef(null);
  const { ref: inViewRef, inView } = useInView();

  const combinedRef = (el) => {
    rowRef.current = el;
    inViewRef.current = el;
  };

  const DOT_R = 7;
  const imagePath = resolveEventImagePath(item.image);
  const { src: imageSrc } = makeImageSrc(imagePath);

  const renderImageBox = (justifyContent) => {
    if (!imageSrc) {
      return null;
    }

    return (
      <Slide in={inView} direction="up" timeout={600}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: {
                xs: 150,
                sm: 275,
                md: 280,
              },
              bgcolor: "transparent",
              p: {
                xs: 0.5,
                md: 1.5,
              },
            }}
          >
            <Box
              component="img"
              src={imageSrc}
              alt={`${item.years} ${item.title}`}
              sx={{
                width: "100%",
                borderRadius: 1,
                display: "block",
                objectFit: "contain",
              }}
            />
          </Paper>
        </Box>
      </Slide>
    );
  };

  const renderTextBox = (align) => (
    <Slide in={inView} direction="up" timeout={600}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 520,
          bgcolor: "transparent",
          p: { xs: 1, sm: 1.25, md: 1.5 },
          textAlign: align,
        }}
      >
        <Typography
          sx={{
            fontFamily: fonts,
            fontWeight: 700,
            fontSize: { xs: 18, sm: 30, md: 20 },
            mb: 0.75,
            color: rail,
          }}
        >
          {item.years}
          <br />
          {item.title}
        </Typography>

        <Typography
          sx={{
            fontFamily: fonts,
            fontSize: { xs: 14, sm: 19, md: 15.5 },
            lineHeight: 1.9,
            color: "text.secondary",
          }}
        >
          {renderContentLines(item.content)}
        </Typography>
      </Paper>
    </Slide>
  );

  return (
    <Box
      ref={combinedRef}
      sx={{
        position: "relative",

        display: "grid",

        gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",

        columnGap: {
          xs: 2,
          md: 3,
        },

        alignItems: "center",

        minHeight: {
          xs: 150,
          md: 180,
        },

        mt: {
          xs: 0,
          md: 0,
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: DOT_R * 2,
          height: DOT_R * 2,
          borderRadius: "50%",
          bgcolor: "#fff",
          border: `3px solid ${rail}`,
          zIndex: 3,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          gridColumn: "1 / 2",
          display: "flex",
          justifyContent: "flex-end",
          pr: { xs: 1, md: 2 },
        }}
      >
        {isLeft ? renderTextBox("right") : renderImageBox("flex-end")}
      </Box>

      <Box
        sx={{
          gridColumn: "2 / 3",
          display: "flex",
          justifyContent: "flex-start",
          pl: { xs: 1, md: 2 },
        }}
      >
        {isLeft ? renderImageBox("flex-start") : renderTextBox("left")}
      </Box>
    </Box>
  );
}
function HistoryFinalEventRow({ item, rail, fonts, rootRef }) {
  const rowRef = useRef(null);
  const { ref: inViewRef, inView } = useInView();

  const combinedRef = (el) => {
    rowRef.current = el;
    inViewRef.current = el;

    if (rootRef) {
      rootRef.current = el;
    }
  };

  const DOT_R = 8;
  const { src: finalLogoSrc } = makeImageSrc("/img/Logo/LOGO-05.webp");

  return (
    <Box
      ref={combinedRef}
      sx={{
        position: "relative",

        pt: {
          xs: 5,
          md: 7,
        },

        pb: {
          xs: 8,
          md: 12,
        },

        textAlign: "center",
      }}
    >
      {/* Final dot where the center timeline ends. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",

          top: {
            xs: 18,
            md: 26,
          },

          left: "50%",

          transform: "translate(-50%, -50%)",

          width: DOT_R * 2,
          height: DOT_R * 2,

          borderRadius: "50%",

          bgcolor: rail,

          border: `3px solid ${rail}`,

          zIndex: 3,

          pointerEvents: "none",
        }}
      />

      <Slide in={inView} direction="up" timeout={600}>
        <Box
          sx={{
            position: "relative",

            zIndex: 4,

            maxWidth: 760,

            mx: "auto",

            px: {
              xs: 3,
              md: 5,
            },

            pt: {
              xs: 5,
              md: 7,
            },

            textAlign: "center",
          }}
        >
          {finalLogoSrc && (
            <Box
              component="img"
              src={finalLogoSrc}
              alt="SEHO"
              sx={{
                display: "block",

                width: {
                  xs: 220,
                  sm: 350,
                  md: 300,
                  lg: 340,
                },

                maxWidth: "80%",

                mx: "auto",

                mb: {
                  xs: 2.25,
                  md: 2.75,
                },
              }}
            />
          )}

          <Typography
            sx={{
              fontFamily: fonts,

              fontWeight: 700,

              fontSize: {
                xs: 34,
                sm: 40,
                md: 48,
                lg: 35,
              },

              lineHeight: 1.1,

              color: rail,

              mb: 1,
            }}
          >
            {item.years}
          </Typography>

          <Typography
            sx={{
              fontFamily: fonts,

              fontWeight: 700,

              fontSize: {
                xs: 21,
                sm: 28,
                md: 28,
                lg: 30,
              },

              lineHeight: 1.35,

              color: rail,

              mb: {
                xs: 2,
                md: 2.5,
              },
            }}
          >
            {item.title}
          </Typography>

          <Typography
            sx={{
              fontFamily: fonts,

              fontSize: {
                xs: 14,
                sm: 22,
                md: 16,
                lg: 20,
              },

              lineHeight: 1.9,

              color: "text.secondary",

              maxWidth: 760,

              mx: "auto",
            }}
          >
            {renderContentLines(item.content)}
          </Typography>
        </Box>
      </Slide>
    </Box>
  );
}

/* --------- main timeline component --------- */
export default function HistoryTimeline({
  mode = "b2b",
  fonts = '"LINE Sans TW","Noto Sans TC",system-ui',
  bg = "background.default",
  rail,
  lang = "ZH", // <- ZH / EN / JP, passed from HistorySEHO
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const historyData = useMemo(() => getHistoryData(lang), [lang]);
  const { title, subtitle, introLines } = getTimelineText(lang);

  const firstHeroDescription = useMemo(() => {
    const textByLang = {
      ZH: "以水禾之名，淨化環境、滋養生活。台灣水禾深耕食品衛生，堅持品質與服務，致力提升台灣生活水平。",
      EN: "Under the name SEHO, we purify environments and enrich daily life. Taiwan SEHO has long focused on food hygiene, upholding quality and service while improving living standards.",
      JP: "水禾の名のもと、環境を浄化し、暮らしを育む。台湾水禾は食品衛生に深く取り組み、品質とサービスを大切にしながら生活水準の向上に努めています。",
    };

    return textByLang[lang] || textByLang.ZH;
  }, [lang]);

  const flatRows = useMemo(() => {
    const rows = [];

    historyData.forEach((block, blockIndex) => {
      const children = block.detail || block.children || [];
      if (!children.length) return;

      const isFirstPeriod = blockIndex === 0;
      const isLastPeriod = blockIndex === historyData.length - 1;

      if (isFirstPeriod) {
        rows.push({
          type: "firstPeriodHero",
          period: block.period,
          years: block.years,
          content: block.content,
          image: block.image,
        });
      }

      children.forEach((it) => {
        rows.push({
          type: isLastPeriod ? "finalEvent" : "event",
          period: block.period,
          title: it.title,
          years: it.years,
          content: it.content,
          image: it.image,
        });
      });
    });

    return rows;
  }, [historyData]);

  const timelineContentRef = useRef(null);
  const finalEventRef = useRef(null);
  const [centerRailHeight, setCenterRailHeight] = useState(null);

  useEffect(() => {
    const updateCenterRailHeight = () => {
      const timelineEl = timelineContentRef.current;
      const finalEl = finalEventRef.current;

      if (!timelineEl || !finalEl) {
        return;
      }

      const timelineRect = timelineEl.getBoundingClientRect();
      const finalRect = finalEl.getBoundingClientRect();

      const finalDotTop = window.matchMedia("(min-width: 900px)").matches
        ? 26
        : 18;

      const nextHeight = Math.max(
        0,
        finalRect.top - timelineRect.top + finalDotTop,
      );

      setCenterRailHeight((currentHeight) => {
        if (
          typeof currentHeight === "number" &&
          Math.abs(currentHeight - nextHeight) < 0.5
        ) {
          return currentHeight;
        }

        return nextHeight;
      });
    };

    const frameId = window.requestAnimationFrame(updateCenterRailHeight);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateCenterRailHeight)
        : null;

    if (resizeObserver) {
      if (timelineContentRef.current) {
        resizeObserver.observe(timelineContentRef.current);
      }

      if (finalEventRef.current) {
        resizeObserver.observe(finalEventRef.current);
      }
    }

    window.addEventListener("resize", updateCenterRailHeight);
    window.addEventListener("load", updateCenterRailHeight);

    return () => {
      window.cancelAnimationFrame(frameId);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      window.removeEventListener("resize", updateCenterRailHeight);
      window.removeEventListener("load", updateCenterRailHeight);
    };
  }, [flatRows]);

  let eventIndex = -1;

  return (
    <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: bg, position: "relative" }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          maxWidth: "100%",
          mx: "auto",
          px: 0,
          position: "relative",
          overflowX: "hidden",
        }}
      >
        {/* main column: title + hero + events */}
        <Box sx={{ width: "100%" }}>
          <Box>
            {/* title block */}
            <Box
              sx={{
                mb: { xs: 4, md: 6 },
                textAlign: "center",
                px: { xs: 2, md: 4 },
              }}
            >
              <Typography
                sx={{
                  fontFamily: fonts,
                  fontSize: { xs: 22, md: 26 },
                  fontWeight: 700,
                  mb: 1,
                  color: rail,
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: fonts,
                  fontSize: { xs: 14, md: 16 },
                  lineHeight: 1.9,
                  fontWeight: 700,
                  color: "text.secondary",
                }}
              >
                {subtitle}
              </Typography>
            </Box>

            {/* timeline content (rail + hero + events) */}
            <Box
              ref={timelineContentRef}
              sx={{
                position: "relative",
                pb: { xs: 4, md: 6 },
              }}
            >
              {/* main center rail - ends exactly at the final timeline dot */}
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: 0,

                  height:
                    centerRailHeight == null ? "100%" : `${centerRailHeight}px`,

                  width: 3,
                  bgcolor: rail,
                  transform: "translateX(-50%)",
                  zIndex: 0,
                }}
              />

              <Stack spacing={{ xs: 4, md: 5 }}>
                {flatRows.map((row, idx) => {
                  if (row.type === "firstPeriodHero") {
                    const heroPath = resolveHeroImagePath(row.image, isDesktop);
                    const { src } = makeImageSrc(heroPath);
                    const displayPeriod = String(row.period || "").replace(
                      /期$/,
                      "",
                    );

                    return (
                      <Box
                        key={`first-period-${row.period}-${row.years}`}
                        sx={{
                          position: "relative",
                          width: "100%",
                          maxWidth: "none",
                          mx: 0,
                          mb: { xs: 4, md: 7 },
                          zIndex: 2,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            overflow: "hidden",
                          }}
                        >
                          {src && (
                            <Box
                              component="img"
                              src={src}
                              alt={`${row.years} ${row.period}`}
                              sx={{
                                display: "block",
                                width: "100%",
                                height: "auto",
                                maxWidth: "none",
                              }}
                            />
                          )}

                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,

                              display: "flex",

                              alignItems: {
                                xs: "flex-start",
                                md: "center",
                              },

                              pt: {
                                xs: 2,
                                sm: 2.5,
                                md: 0,
                              },

                              boxSizing: "border-box",

                              pointerEvents: "none",
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                maxWidth: 1200,
                                mx: "auto",

                                px: {
                                  xs: 3,
                                  md: "30%",
                                  lg: 0,
                                },

                                display: "flex",

                                justifyContent: {
                                  xs: "center",
                                  md: "center",
                                  lg: "center",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: {
                                    xs: "100%",
                                    md: "80%",
                                    lg: "50%",
                                  },

                                  maxWidth: 620,

                                  color: "#ffffff",

                                  textAlign: {
                                    xs: "center",
                                    md: "left",
                                  },

                                  transform: {
                                    xs: "none",
                                    md: "translateY(-2px)",
                                    lg: "translateY(-4px)",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",

                                    flexDirection: {
                                      xs: "column",
                                      md: "row",
                                    },

                                    alignItems: {
                                      xs: "center",
                                      md: "baseline",
                                    },

                                    gap: {
                                      xs: 0.5,
                                      md: 2,
                                    },

                                    mb: {
                                      xs: 1.25,
                                      md: 1.5,
                                    },
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: fonts,
                                      fontSize: {
                                        xs: 34,
                                        sm: 45,
                                        md: 42,
                                        lg: 46,
                                      },
                                      fontWeight: 400,
                                      lineHeight: {xs: 1, sm: 1.5, md: 1.25, lg: 1},
                                    }}
                                  >
                                    {row.years}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      fontFamily: fonts,
                                      fontSize: {
                                        xs: 20,
                                        sm: 30,
                                        md: 24,
                                        lg: 26,
                                      },
                                      fontWeight: 400,
                                      lineHeight: {xs: 1, sm: 1.5, md: 1.25, lg: 1.25},
                                    }}
                                  >
                                    {displayPeriod}
                                  </Typography>
                                </Box>

                                <Typography
                                  sx={{
                                    fontFamily: fonts,
                                    fontSize: {
                                      xs: 15,
                                      sm: 25,
                                      md: 16,
                                      lg: 18,
                                    },
                                    fontWeight: 400,
                                    lineHeight: {
                                      xs: 1.7,
                                      sm: 1.7,
                                      md: 1.7,
                                    },
                                  }}
                                >
                                  {firstHeroDescription}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  }

                  if (row.type === "finalEvent") {
                    return (
                      <HistoryFinalEventRow
                        key={`final-event-${row.period}-${row.years}-${idx}`}
                        item={row}
                        rail={rail}
                        fonts={fonts}
                        rootRef={finalEventRef}
                      />
                    );
                  }

                  // event rows
                  eventIndex += 1;
                  const isLeft = eventIndex % 2 === 0;

                  return (
                    <HistoryEventRow
                      key={`event-${row.period}-${row.years}-${idx}`}
                      item={row}
                      isLeft={!isLeft}
                      rail={rail}
                      fonts={fonts}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
